import React from 'react';
import PROJECTS, { BAND_ORDER } from '../config/projects';

// The dense sortable / filterable projects table (Signature look). Live demos
// lead by default. Rows expand into a left-rail metadata + right-pane
// problem -> approach -> tradeoff + auth/security note (sharpening adj. 2).

const FILTERS = [
  { key: 'all', label: 'all' },
  { key: 'Live', label: 'live demos' },
  { key: 'Tooling', label: 'tooling & infra' },
  { key: 'Domain', label: 'domain demos' },
];

function defaultSort(a, b) {
  return BAND_ORDER[a.band] - BAND_ORDER[b.band];
}

export default function ProjectsReal() {
  const [filter, setFilter] = React.useState('all');
  const [sortKey, setSortKey] = React.useState(null);
  const [sortDir, setSortDir] = React.useState(1);
  const [openRow, setOpenRow] = React.useState(null);

  const rows = React.useMemo(() => {
    let data = PROJECTS.filter((p) => filter === 'all' || p.band === filter);
    if (sortKey) {
      data = [...data].sort((a, b) => {
        let av = a[sortKey] || '';
        let bv = b[sortKey] || '';
        if (sortKey === 'live') {
          av = a.live ? '0' : '1';
          bv = b.live ? '0' : '1';
        }
        return av.toString().localeCompare(bv.toString()) * sortDir;
      });
    } else {
      data = [...data].sort(defaultSort);
    }
    return data;
  }, [filter, sortKey, sortDir]);

  function onSort(key) {
    if (sortKey === key) {
      setSortDir((d) => d * -1);
    } else {
      setSortKey(key);
      setSortDir(1);
    }
  }

  function sortAttr(key) {
    if (sortKey !== key) return undefined;
    return sortDir === 1 ? 'ascending' : 'descending';
  }

  const cols = [
    { key: 'name', label: 'Project' },
    { key: 'stack', label: 'Stack' },
    { key: 'type', label: 'Type' },
    { key: 'live', label: 'Live' },
    { key: 'repo', label: 'Repo' },
  ];

  return (
    <>
      <div className="controls">
        <span className="label">band</span>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className="chip"
            aria-pressed={filter === f.key}
            onClick={() => {
              setFilter(f.key);
              setOpenRow(null);
            }}
          >
            {f.label}
          </button>
        ))}
        <span className="count">
          {rows.length} of {PROJECTS.length} shown
        </span>
      </div>

      <div className="tablewrap">
        <table id="proj">
          <thead>
            <tr>
              {cols.map((c) => (
                <th
                  key={c.key}
                  tabIndex={0}
                  aria-sort={sortAttr(c.key)}
                  onClick={() => onSort(c.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSort(c.key);
                    }
                  }}
                >
                  {c.label}
                  <span className="arrow" aria-hidden="true">↕</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const isOpen = openRow === p.name;
              return (
                <React.Fragment key={p.name}>
                  <tr
                    className="expandable"
                    aria-expanded={isOpen}
                    onClick={() => setOpenRow(isOpen ? null : p.name)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenRow(isOpen ? null : p.name);
                      }
                    }}
                    tabIndex={0}
                  >
                    <td className="name">
                      <span className="twirl" aria-hidden="true">
                        {isOpen ? '▾' : '▸'}
                      </span>
                      {p.name}
                      <span className="desc">{p.desc}</span>
                    </td>
                    <td className="stack">{p.stack}</td>
                    <td className="type">
                      {p.live ? <span className="pill">live</span> : p.type}
                    </td>
                    <td className="demo">
                      {p.live ? (
                        <>
                          <a
                            href={p.live}
                            rel="noopener"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {p.live.replace('https://', '')}
                          </a>
                          {p.liveNote && (
                            <span className="livenote">{p.liveNote}</span>
                          )}
                        </>
                      ) : (
                        <span className="none">code only</span>
                      )}
                    </td>
                    <td>
                      <a
                        className="repo-link"
                        href={`https://github.com/${p.repo}`}
                        rel="noopener"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {p.repo}
                      </a>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="detail">
                      <td colSpan={5}>
                        <div className="detail-pane">
                          <div className="detail-meta">
                            <dl>
                              <dt>stack</dt>
                              <dd>{p.stack}</dd>
                              <dt>role</dt>
                              <dd>{p.role}</dd>
                              <dt>repo</dt>
                              <dd>
                                <a
                                  href={`https://github.com/${p.repo}`}
                                  rel="noopener"
                                >
                                  {p.repo}
                                </a>
                              </dd>
                              {p.live && (
                                <>
                                  <dt>live</dt>
                                  <dd>
                                    <a href={p.live} rel="noopener">
                                      {p.live.replace('https://', '')}
                                    </a>
                                    {p.liveNote && (
                                      <span className="livenote">
                                        {p.liveNote}
                                      </span>
                                    )}
                                  </dd>
                                </>
                              )}
                            </dl>
                          </div>
                          <div className="detail-body">
                            <h4>problem</h4>
                            <p>{p.problem}</p>
                            <h4>approach</h4>
                            <p>{p.approach}</p>
                            <h4>trade-off</h4>
                            <p>{p.tradeoff}</p>
                            <h4>auth / security</h4>
                            <div className="sec-note">{p.security}</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}
